import type {
  AutocompleteApi,
  AutocompleteOptions,
} from '@algolia/autocomplete-js'
import { autocomplete } from '@algolia/autocomplete-js'
import '@algolia/autocomplete-theme-classic'
import type { MutableRefObject } from 'react'
import { createElement, Fragment, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'

type StyledDivComponent =
  | React.ForwardRefExoticComponent<
      React.HTMLAttributes<HTMLDivElement> & React.RefAttributes<HTMLDivElement>
    >
  | React.ComponentType<React.HTMLAttributes<HTMLDivElement>>

export type AutocompleteProps = {
  autocompleteRef?: MutableRefObject<AutocompleteApi<any> | null>
  // A custom wrapper component for the autocomplete root that can receive a ref.
  StyledDiv?: StyledDivComponent
} & Omit<AutocompleteOptions<any>, 'container' | 'renderer' | 'render'>

export function Autocomplete({
  autocompleteRef,
  StyledDiv,
  ...options
}: AutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<any>(null)

  useEffect(() => {
    if (!containerRef.current) {
      return undefined
    }

    const search = autocomplete({
      container: containerRef.current,
      renderer: { createElement, Fragment },
      render({ children }, root) {
        if (!rootRef.current) {
          rootRef.current = createRoot(root)
        }
        rootRef.current.render(children as any)
      },

      ...options,
    })

    if (autocompleteRef) {
      autocompleteRef.current = search
    }

    return () => {
      if (autocompleteRef) {
        autocompleteRef.current = null
      }
      if (rootRef.current) {
        rootRef.current.unmount()
        rootRef.current = null
      }
      search.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (StyledDiv) {
    return <StyledDiv ref={containerRef as any} />
  }
  return <div ref={containerRef} />
}
